import { useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { Share } from "lucide-react"
import { shareReceipt } from "@/features/staff/receipt-share"
import { billTone, orderTone, paymentTone } from "@/lib/status-colors"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import { ResponsiveDrawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, } from "@/components/ui/drawer"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { InvoicePanel } from "@/features/staff/invoices/invoice-panel"
import type { BillInvoice, CfdiSettings } from "@/features/staff/invoices/invoices"
export type StaffBillPendingPaymentAttempt = {
    id: number
    status: string
    paymentMethod: string
    totalAmount: string
    selectedOrderIds: number[]
}
export type StaffBillOrder = {
    id: number
    name: string
    quantity: number
    status: string
    statusLabel: string
    paymentStatus: string
    paymentStatusLabel: string
    serviceType: string
    serviceTypeLabel: string
    lineTotal: string
    currency: string
    notes: string | null
    customerName: string
}
export type StaffBillRow = {
    id: number
    status: string
    statusLabel: string
    tableNames: string[]
    ordersCount: number
    unpaidOrdersCount: number
    subtotal: string
    tip: string
    total: string
    createdAt: string
    updatedAt: string
    pendingPaymentAttempt: StaffBillPendingPaymentAttempt | null
    orders: StaffBillOrder[]
    allowedActions: {
        canClose: boolean
        canCreatePaymentAttempt: boolean
        canResolvePendingAttempt: boolean
    }
    /**
     * The CFDI currently claiming this bill, or null. "Currently claiming" is
     * `location_invoice_bills.live`, which is the same flag the partial unique
     * index enforces "one live CFDI per bill" with — so a failed invoice is
     * still here, and the drawer shows it rather than offering to open a second.
     */
    invoice?: BillInvoice | null
}
function formatCurrency(amount: string, currency = "MXN") {
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(Number(amount))
}
function formatDateTime(value: string) {
    return new Intl.DateTimeFormat("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value))
}
function buildIdempotencyKey() {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID()
    }
    return `pa_${Date.now()}_${Math.random().toString(36).slice(2)}`
}
export function BillDetailDrawer({ bill, open, onOpenChange, cfdi, timeZone, }: {
    bill: StaffBillRow | null
    open: boolean
    onOpenChange: (open: boolean) => void
    /**
     * null when the restaurant does not have `cfdi_invoicing` switched on, and
     * then the section is not rendered at all — the manage screens' rule that a
     * control for a feature nobody has is noise rather than information.
     */
    cfdi?: CfdiSettings | null
    /** The restaurant's IANA zone; a stamp is timestamped where the restaurant is. */
    timeZone?: string
}) {
    const [submitting, setSubmitting] = useState(false)
    const [sharing, setSharing] = useState(false)
    if (!bill)
        return null
    const handleCloseBill = () => {
        setSubmitting(true)
        router.post(`/bills/${bill.id}/close`, {}, {
            preserveScroll: true,
            preserveState: true,
            onFinish: () => setSubmitting(false),
        })
    }
    const handleCreatePaymentAttempt = (paymentMethod: "cash" | "physical_card_terminal") => {
        setSubmitting(true)
        router.post(`/bills/${bill.id}/payment_attempts`, {
            payment_method: paymentMethod,
            tip_percentage: 0,
            idempotency_key: buildIdempotencyKey(),
        }, {
            preserveScroll: true,
            preserveState: true,
            onFinish: () => setSubmitting(false),
        })
    }
    const handleResolvePaymentAttempt = (status: "succeeded" | "failed") => {
        if (!bill.pendingPaymentAttempt)
            return
        setSubmitting(true)
        patchInline(`/payment_attempts/${bill.pendingPaymentAttempt.id}`, { status }, { preserveState: true })
            .then((result) => toastInlineUpdateSuccess(result))
            .catch((error: unknown) => toastInlineUpdateError(error))
            .finally(() => setSubmitting(false))
    }
    // Sending the receipt. Only on a bill that settled: there is no receipt for
    // money nobody paid, and the route refuses one anyway.
    //
    // Nothing is said on success or on cancel -- the share sheet already showed
    // the person what happened, and a toast on top of it is noise. A download is
    // the one outcome worth naming, because the file went somewhere they did not
    // choose.
    const handleShareReceipt = () => {
        setSharing(true)
        shareReceipt({ id: bill.id, total: bill.total })
            .then((outcome) => {
            if (outcome === "downloaded")
                toast.success(t("ui.react.features.staff.receipt_share.downloaded"))
        })
            .catch(() => toast.error(t("ui.react.features.staff.receipt_share.failed")))
            .finally(() => setSharing(false))
    }
    return (<ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="pb-2">
          <DrawerTitle>
            <span className="font-normal text-muted-foreground">#{bill.id}</span>
            {" · "}{t("ui.react.features.staff.bill_detail_drawer.bill")}</DrawerTitle>
          <DrawerDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <StatusBadge tone={billTone(bill.status)} label={bill.statusLabel}/>
              <span className="text-xs text-muted-foreground">
                {bill.tableNames.length > 0 ? bill.tableNames.join(", ") : t("ui.common.no_table")}
              </span>
            </div>
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-4">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.subtotal")}</dt>
            <dd>{formatCurrency(bill.subtotal)}</dd>

            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.tip")}</dt>
            <dd>{formatCurrency(bill.tip)}</dd>

            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.total")}</dt>
            <dd className="font-medium">{formatCurrency(bill.total)}</dd>

            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.orders")}</dt>
            <dd>{bill.ordersCount}</dd>

            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.items_pay")}</dt>
            <dd>{bill.unpaidOrdersCount}</dd>

            <dt className="text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.updated")}</dt>
            <dd>{formatDateTime(bill.updatedAt)}</dd>
          </dl>

          {bill.pendingPaymentAttempt ? (<section className="space-y-3 rounded-lg border border-warning/40 bg-warning/10 p-4" data-testid={`bill-pending-attempt-${bill.id}`}>
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("ui.react.features.staff.bill_detail_drawer.payment_progress")}</p>
                <p className="text-sm text-muted-foreground">
                  {bill.pendingPaymentAttempt.paymentMethod}{t("ui.react.features.staff.bill_detail_drawer.text")}{formatCurrency(bill.pendingPaymentAttempt.totalAmount)}.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {bill.pendingPaymentAttempt.paymentMethod === "digital_in_app" ? (<p className="text-sm">{t("ui.react.features.staff.bill_detail_drawer.digital_payment_auto_confirmed")}</p>) : (<>
                    <Button type="button" size="sm" variant="outline" disabled={submitting} onClick={() => handleResolvePaymentAttempt("succeeded")}>{t("ui.react.features.staff.bill_detail_drawer.confirm_payment")}</Button>
                    <Button type="button" size="sm" variant="destructive" disabled={submitting} onClick={() => handleResolvePaymentAttempt("failed")}>{t("ui.react.features.staff.bill_detail_drawer.mark_failure")}</Button>
                  </>)}
              </div>
            </section>) : null}

          {bill.status === "paid" && bill.tableNames.length > 0 ? (<p className="rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-success">{t("ui.react.features.staff.bill_detail_drawer.bill_paid_manual_release")}</p>) : null}

          {cfdi ? (<InvoicePanel billId={bill.id} billPaid={bill.status === "paid"} invoice={bill.invoice ?? null} cfdi={cfdi} timeZone={timeZone}/>) : null}

          <section className="space-y-3">
            <div>
              <p className="text-sm font-medium">{t("ui.react.features.staff.bill_detail_drawer.detail_orders")}</p>
              <p className="text-xs text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.status_operational_payment_line")}</p>
            </div>

            {bill.orders.length === 0 ? (<p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.bill_empty")}</p>) : (<ul className="space-y-2">
                {bill.orders.map((order) => (<li key={order.id} className="rounded-lg border bg-card p-3 shadow-xs">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm font-medium">
                          {order.quantity}{t("ui.react.features.staff.bill_detail_drawer.x")}{order.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {order.customerName} · {order.serviceTypeLabel} · {formatCurrency(order.lineTotal, order.currency)}
                        </p>
                        {order.notes ? (<p className="text-xs text-muted-foreground">{t("ui.react.features.staff.bill_detail_drawer.note")}{order.notes}</p>) : null}
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <StatusBadge tone={orderTone(order.status)} label={order.statusLabel}/>
                        <StatusBadge tone={paymentTone(order.paymentStatus)} label={order.paymentStatusLabel}/>
                      </div>
                    </div>
                  </li>))}
              </ul>)}
          </section>
        </div>

        <DrawerFooter className="pt-2">
          {bill.status === "paid" ? (<Button type="button" disabled={sharing} onClick={handleShareReceipt} data-testid={`bill-share-receipt-button-${bill.id}`}>
              <Share />
              {sharing
                ? t("ui.react.features.staff.receipt_share.sharing")
                : t("ui.react.features.staff.receipt_share.share")}
            </Button>) : null}

          {bill.allowedActions.canClose ? (<Button type="button" variant="outline" disabled={submitting} onClick={handleCloseBill} data-testid={`bill-close-button-${bill.id}`}>{t("ui.react.features.staff.bill_detail_drawer.close_bill")}</Button>) : null}

          {bill.allowedActions.canCreatePaymentAttempt ? (<>
              <Button type="button" disabled={submitting} onClick={() => handleCreatePaymentAttempt("cash")} data-testid={`bill-cash-button-${bill.id}`}>{t("ui.react.features.staff.bill_detail_drawer.charge_cash")}</Button>
              <Button type="button" variant="outline" disabled={submitting} onClick={() => handleCreatePaymentAttempt("physical_card_terminal")} data-testid={`bill-terminal-button-${bill.id}`}>{t("ui.react.features.staff.bill_detail_drawer.charge_terminal")}</Button>
            </>) : null}
        </DrawerFooter>
      </DrawerContent>
    </ResponsiveDrawer>)
}
